import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Key, Globe, Bell, Shield, Plus, Trash2, Eye, EyeOff, Copy, Check,
  ExternalLink, ChevronRight, AlertCircle, CheckCircle, Loader2,
  Terminal, Zap, Lock, RefreshCw, X, Settings2, Building2, Cpu,
} from 'lucide-react'
import { Card, Badge, PageHeader } from '../components/Card'
import {
  listIntegrations, setCredentials, deleteCredentials,
  testCredentials, getOAuthUrl,
  listConnectedAccounts, disconnectAccount,
  listApiKeys, createApiKey, revokeApiKey,
  type PlatformStatus, type CredentialFieldDef, type ConnectedAccount,
  type ApiToken, type NewApiToken,
} from '../api/client'

// ─── Tab types ──────────────────────────────────────────────────────────────

type Tab = 'platforms' | 'credentials' | 'tokens' | 'guide' | 'workspace' | 'llm' | 'alerts'

// ─── Platform category colors ────────────────────────────────────────────────

const PLATFORM_COLORS: Record<string, string> = {
  '#000000': 'bg-neutral-900',
  '#0A66C2': 'bg-blue-700',
  '#1877F2': 'bg-blue-600',
  '#E1306C': 'bg-pink-600',
  '#010101': 'bg-neutral-950',
  '#FF0000': 'bg-red-600',
  '#E60023': 'bg-rose-600',
  '#FF4500': 'bg-orange-600',
  '#10A37F': 'bg-emerald-600',
  '#D97757': 'bg-orange-500',
}

function platformBg(color: string) {
  return PLATFORM_COLORS[color] ?? 'bg-slate-700'
}

// ─── Copy to Clipboard button ────────────────────────────────────────────────

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 2000) }}
      className="p-1.5 rounded hover:bg-slate-700 text-slate-500 hover:text-slate-300 transition-colors"
      title="Copy to clipboard"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  )
}

// ─── Secret field with reveal toggle ────────────────────────────────────────

function SecretField({
  value, onChange, placeholder, disabled = false,
}: {
  value: string; onChange: (v: string) => void; placeholder?: string; disabled?: boolean
}) {
  const [show, setShow] = useState(false)
  return (
    <div className="relative flex items-center">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 pr-9 text-sm font-mono
                   text-slate-200 placeholder-slate-600 focus:outline-none focus:border-brand-500 transition-colors
                   disabled:opacity-50 disabled:cursor-not-allowed"
      />
      <button
        type="button"
        onClick={() => setShow(s => !s)}
        className="absolute right-2.5 text-slate-500 hover:text-slate-300 transition-colors"
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  )
}

// ─── Inline terminal snippet ─────────────────────────────────────────────────

function CliSnippet({ code }: { code: string }) {
  return (
    <div className="flex items-center gap-2 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2">
      <Terminal className="w-3.5 h-3.5 text-brand-400 shrink-0" />
      <code className="text-xs text-slate-300 font-mono flex-1 min-w-0 overflow-x-auto whitespace-nowrap select-all">{code}</code>
      <CopyButton value={code} />
    </div>
  )
}

// ─── ────────────────────────────────────────────────────────────────────────
//     TAB 1 — Platform Cards
// ─── ────────────────────────────────────────────────────────────────────────

function PlatformCard({
  platform,
  onConfigure,
  onOAuth,
}: {
  platform: PlatformStatus
  onConfigure: (p: PlatformStatus) => void
  onOAuth: (p: PlatformStatus) => void
}) {
  const qc = useQueryClient()
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ status: string; message: string } | null>(null)

  const handleTest = useCallback(async () => {
    setTesting(true)
    setTestResult(null)
    try {
      const r = await testCredentials(platform.id)
      setTestResult(r)
    } catch (e: unknown) {
      setTestResult({ status: 'error', message: (e as Error).message })
    } finally {
      setTesting(false)
    }
  }, [platform.id])

  const deleteMut = useMutation({
    mutationFn: () => deleteCredentials(platform.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations'] }),
  })

  const statusColor = platform.has_credentials ? 'green' : 'slate'
  const statusLabel = platform.has_credentials ? 'Configured' : 'Not configured'

  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 py-4 border-b border-slate-800/60 last:border-0">
      {/* Avatar + info */}
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-white font-bold text-sm ${platformBg(platform.color)}`}>
          {platform.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
            <span className="text-sm font-semibold text-slate-200">{platform.label}</span>
            <Badge color={statusColor}>{statusLabel}</Badge>
            {platform.category === 'llm' && <Badge color="purple">LLM</Badge>}
            {platform.connected_count > 0 && (
              <Badge color="blue">{platform.connected_count} {platform.account_noun}{platform.connected_count !== 1 ? 's' : ''}</Badge>
            )}
          </div>
          <p className="text-xs text-slate-500">{platform.fields.map((f: CredentialFieldDef) => f.label).join(' · ')}</p>
          {testResult && (
            <div className={`mt-2 flex items-center gap-1.5 text-xs ${testResult.status === 'valid' ? 'text-green-400' : 'text-red-400'}`}>
              {testResult.status === 'valid' ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
              {testResult.message}
            </div>
          )}
        </div>
      </div>
      {/* Action buttons — indented on mobile to align under the text column */}
      <div className="flex items-center gap-1.5 flex-wrap pl-[52px] sm:pl-0 sm:shrink-0">
        {platform.has_credentials && (
          <>
            <button
              onClick={handleTest}
              disabled={testing}
              className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-700
                         text-slate-400 hover:text-slate-200 hover:border-slate-600 transition-colors disabled:opacity-50"
            >
              {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Test
            </button>
            {platform.oauth_supported && (
              <button
                onClick={() => onOAuth(platform)}
                className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-brand-700
                           text-brand-300 hover:bg-brand-900/30 transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Connect
              </button>
            )}
            <button
              onClick={() => deleteMut.mutate()}
              className="p-1.5 rounded-lg hover:bg-red-900/30 text-slate-600 hover:text-red-400 transition-colors"
              title="Remove credentials"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </>
        )}
        <button
          onClick={() => onConfigure(platform)}
          className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors"
        >
          <Settings2 className="w-3.5 h-3.5" />
          {platform.has_credentials ? 'Edit' : 'Setup'}
        </button>
      </div>
    </div>
  )
}

// ─── Configure credential modal ──────────────────────────────────────────────

function ConfigureModal({ platform, onClose }: { platform: PlatformStatus; onClose: () => void }) {
  const qc = useQueryClient()
  const [values, setValues] = useState<Record<string, string>>(
    Object.fromEntries(platform.fields.map((f: CredentialFieldDef) => [f.key, ''])),
  )
  const [saved, setSaved] = useState(false)

  const mut = useMutation({
    mutationFn: () => setCredentials(platform.id, values),
    onSuccess: () => {
      setSaved(true)
      qc.invalidateQueries({ queryKey: ['integrations'] })
      setTimeout(onClose, 1200)
    },
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-800">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-sm shrink-0 ${platformBg(platform.color)}`}>
            {platform.icon}
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-white">{platform.label} Credentials</h3>
            <p className="text-xs text-slate-500">Encrypted at rest with AES-256-GCM</p>
          </div>
          <button onClick={onClose} className="text-slate-600 hover:text-slate-400 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={e => { e.preventDefault(); mut.mutate() }} className="p-5 space-y-4">
          <a href={platform.docs_url} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs text-brand-400 hover:text-brand-300 transition-colors">
            <ExternalLink className="w-3.5 h-3.5" />
            Get your credentials → {platform.label} Developer Console
          </a>
          {platform.fields.map((field: CredentialFieldDef) => (
            <div key={field.key}>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">
                {field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}
              </label>
              {field.secret ? (
                <SecretField
                  value={values[field.key] ?? ''}
                  onChange={v => setValues(prev => ({ ...prev, [field.key]: v }))}
                  placeholder={field.placeholder}
                />
              ) : (
                <input
                  type="text"
                  value={values[field.key] ?? ''}
                  onChange={e => setValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                  placeholder={field.placeholder}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm font-mono
                             text-slate-200 placeholder-slate-600 focus:outline-none focus:border-brand-500 transition-colors"
                />
              )}
              <p className="text-xs text-slate-600 mt-1">{field.help}</p>
            </div>
          ))}
          {mut.isError && (
            <div className="flex items-center gap-2 text-xs text-red-400 bg-red-900/20 rounded-lg px-3 py-2">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              {(mut.error as Error).message}
            </div>
          )}
          {saved && (
            <div className="flex items-center gap-2 text-xs text-green-400 bg-green-900/20 rounded-lg px-3 py-2">
              <CheckCircle className="w-3.5 h-3.5" />
              Credentials saved and encrypted successfully!
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 rounded-lg border border-slate-700 text-sm text-slate-400 hover:text-slate-300 hover:border-slate-600 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={mut.isPending || saved}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-500 text-white text-sm font-medium transition-colors disabled:opacity-60">
              {mut.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              {saved ? 'Saved!' : 'Save & Encrypt'}
              <Lock className="w-3.5 h-3.5" />
            </button>
          </div>
          <p className="text-[11px] text-slate-600 text-center">Stored encrypted · Never logged · AES-256-GCM</p>
        </form>
      </div>
    </div>
  )
}

// ─── OAuth flow modal ────────────────────────────────────────────────────────

function OAuthModal({ platform, authUrl, onClose }: { platform: PlatformStatus; authUrl: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-800">
          <Globe className="w-5 h-5 text-brand-400" />
          <h3 className="text-sm font-semibold text-white flex-1">Connect {platform.label}</h3>
          <button onClick={onClose} className="text-slate-600 hover:text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-400">
            Open the {platform.label} authorization page in a new tab. After granting access, return here.
          </p>
          <div className="bg-slate-950 border border-slate-800 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1">Authorization URL</p>
            <div className="flex items-start gap-2">
              <code className="text-xs text-slate-300 font-mono break-all flex-1 select-all">{authUrl}</code>
              <CopyButton value={authUrl} />
            </div>
          </div>
          <div className="space-y-1.5">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Or via terminal:</p>
            <CliSnippet code={`open "${authUrl}"`} />
          </div>
          <a href={authUrl} target="_blank" rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-500 text-white text-sm font-medium transition-colors">
            <ExternalLink className="w-4 h-4" />
            Open {platform.label} Authorization
          </a>
          <button onClick={onClose}
            className="w-full px-4 py-2 rounded-lg border border-slate-700 text-sm text-slate-400 hover:border-slate-600 hover:text-slate-300 transition-colors">
            Done
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── ────────────────────────────────────────────────────────────────────────
//     TAB 1: Platforms
// ─── ────────────────────────────────────────────────────────────────────────

function PlatformsTab() {
  const [configuring, setConfiguring] = useState<PlatformStatus | null>(null)
  const [oauthData, setOauthData] = useState<{ platform: PlatformStatus; url: string } | null>(null)
  const [loadingOAuth, setLoadingOAuth] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'social' | 'llm'>('all')

  const { data, isLoading, error } = useQuery({
    queryKey: ['integrations'],
    queryFn: listIntegrations,
  })

  const platforms = data?.platforms ?? []
  const connected = platforms.filter(p => p.has_credentials).length

  const filtered = filter === 'all' ? platforms
    : filter === 'social' ? platforms.filter(p => p.category === 'social')
    : platforms.filter(p => p.category === 'llm')

  const handleOAuth = useCallback(async (p: PlatformStatus) => {
    setLoadingOAuth(p.id)
    try {
      const r = await getOAuthUrl(p.id)
      setOauthData({ platform: p, url: r.auth_url })
    } catch (e: unknown) {
      alert(`Could not generate OAuth URL: ${(e as Error).message}`)
    } finally {
      setLoadingOAuth(null)
    }
  }, [])

  if (isLoading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-400" /></div>
  if (error) return <div className="flex items-center gap-2 text-red-400 text-sm p-4"><AlertCircle className="w-4 h-4" />Failed to load: {(error as Error).message}</div>

  return (
    <>
      {configuring && <ConfigureModal platform={configuring} onClose={() => setConfiguring(null)} />}
      {oauthData && <OAuthModal platform={oauthData.platform} authUrl={oauthData.url} onClose={() => setOauthData(null)} />}

      <div className="space-y-5">
        <div className="grid grid-cols-3 gap-3">
          {[
            { value: connected, label: 'Configured' },
            { value: platforms.filter(p => p.category === 'social').length, label: 'Social Platforms' },
            { value: platforms.filter(p => p.category === 'llm').length, label: 'AI / LLM' },
          ].map(s => (
            <div key={s.label} className="bg-slate-800/50 border border-slate-800 rounded-xl p-3 text-center">
              <div className="text-2xl font-bold text-white">{s.value}</div>
              <div className="text-[10px] sm:text-xs text-slate-500 mt-0.5 truncate">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="flex gap-1 bg-slate-900 border border-slate-800 rounded-xl p-1">
          {(['all', 'social', 'llm'] as const).map(t => (
            <button key={t} onClick={() => setFilter(t)}
              className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors capitalize
                          ${filter === t ? 'bg-slate-700 text-white' : 'text-slate-500 hover:text-slate-300'}`}>
              {t === 'all' ? 'All Platforms' : t === 'social' ? 'Social Media' : 'AI / LLM'}
            </button>
          ))}
        </div>

        <Card>
          <div>
            {filtered.map(p => (
              <PlatformCard
                key={p.id}
                platform={p}
                onConfigure={setConfiguring}
                onOAuth={async (pl) => { if (!loadingOAuth) await handleOAuth(pl) }}
              />
            ))}
          </div>
        </Card>

        {/* Connected accounts panel */}
        <ConnectedAccountsPanel />

        <Card title="Terminal Quick-Setup">
          <div className="space-y-2">
            <p className="text-xs text-slate-500 mb-2">Configure any platform directly from the terminal:</p>
            <CliSnippet code="socialscale config set x.api_key LQTo7jQ9IO1eIjwcOtIwEvllM" />
            <CliSnippet code="socialscale config set openai.api_key sk-proj-..." />
            <CliSnippet code="socialscale accounts connect facebook" />
            <CliSnippet code="socialscale accounts list" />
          </div>
        </Card>
      </div>
    </>
  )
}

// ─── ────────────────────────────────────────────────────────────────────────
//     Connected Accounts Panel (shown in PlatformsTab)
// ─── ────────────────────────────────────────────────────────────────────────

function ConnectedAccountsPanel() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['connected-accounts'],
    queryFn: listConnectedAccounts,
    refetchInterval: 10_000,
  })

  const disconnectMut = useMutation({
    mutationFn: (id: string) => disconnectAccount(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['connected-accounts'] })
      qc.invalidateQueries({ queryKey: ['integrations'] })
    },
  })

  const accounts = data?.accounts ?? []

  return (
    <Card
      title="Connected Accounts"
      action={<Badge color={accounts.length > 0 ? 'green' : 'slate'}>{accounts.length} connected</Badge>}
    >
      {isLoading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
        </div>
      ) : accounts.length === 0 ? (
        <div className="py-6 text-center">
          <Globe className="w-8 h-8 text-slate-700 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No accounts connected yet</p>
          <p className="text-xs text-slate-600 mt-1">
            Configure credentials above, then click <strong className="text-slate-400">Connect</strong> to authorize via OAuth
          </p>
        </div>
      ) : (
        <div>
          {accounts.map((acct: ConnectedAccount) => (
            <div key={acct.id} className="flex items-center gap-3 py-3 border-b border-slate-800/50 last:border-0">
              {/* Avatar */}
              {acct.avatar_url ? (
                <img
                  src={acct.avatar_url}
                  alt={acct.display_name}
                  className="w-9 h-9 rounded-full object-cover shrink-0"
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
                />
              ) : (
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 ${platformBg(acct.platform_color)}`}>
                  {acct.platform_icon}
                </div>
              )}

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-200 truncate">{acct.display_name}</span>
                  <Badge color="green">Live</Badge>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-xs text-slate-500">{acct.platform_label}</span>
                  {acct.page_category && (
                    <>
                      <span className="text-slate-700">·</span>
                      <span className="text-xs text-slate-600">{acct.page_category}</span>
                    </>
                  )}
                  <span className="text-slate-700">·</span>
                  <span className="text-xs text-slate-600">@{acct.platform_account_id.length > 16 ? acct.platform_account_id.slice(0, 16) + '…' : acct.platform_account_id}</span>
                </div>
              </div>

              {/* Disconnect */}
              <button
                onClick={() => {
                  if (confirm(`Disconnect ${acct.display_name}? You can reconnect any time.`)) {
                    disconnectMut.mutate(acct.id)
                  }
                }}
                disabled={disconnectMut.isPending}
                className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-700
                           text-slate-500 hover:text-red-400 hover:border-red-900 transition-colors disabled:opacity-50"
                title="Disconnect account"
              >
                <X className="w-3 h-3" />
                Disconnect
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add another account hint */}
      {accounts.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-800/50">
          <p className="text-xs text-slate-600">
            To add another account for the same platform, click <strong className="text-slate-500">Connect</strong> on the platform card above.
          </p>
        </div>
      )}
    </Card>
  )
}

// ─── ────────────────────────────────────────────────────────────────────────
//     TAB 2: Credential Vault
// ─── ────────────────────────────────────────────────────────────────────────

function CredentialBlock({ platform }: { platform: PlatformStatus }) {
  const qc = useQueryClient()
  const [expanded, setExpanded] = useState(false)
  const [editing, setEditing] = useState(false)
  const [values, setValues] = useState<Record<string, string>>({})

  const mut = useMutation({
    mutationFn: () => setCredentials(platform.id, values),
    onSuccess: () => { setEditing(false); qc.invalidateQueries({ queryKey: ['integrations'] }) },
  })

  const deleteMut = useMutation({
    mutationFn: () => deleteCredentials(platform.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations'] }),
  })

  return (
    <div className="border border-slate-800 rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-800/30 transition-colors"
        onClick={() => setExpanded(e => !e)}>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-xs shrink-0 ${platformBg(platform.color)}`}>
          {platform.icon}
        </div>
        <div className="flex-1">
          <span className="text-sm font-medium text-slate-200">{platform.label}</span>
          {platform.updated_at && (
            <span className="text-xs text-slate-600 ml-2">Updated {new Date(platform.updated_at).toLocaleDateString()}</span>
          )}
        </div>
        <Badge color="green">Active</Badge>
        <ChevronRight className={`w-4 h-4 text-slate-600 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </div>
      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t border-slate-800 space-y-3">
          {platform.fields.map((field: CredentialFieldDef) => {
            const storedVal = platform.credential_values[field.key] ?? ''
            return (
              <div key={field.key}>
                <label className="text-xs text-slate-500 mb-1 block">{field.label}</label>
                {editing ? (
                  field.secret ? (
                    <SecretField value={values[field.key] ?? ''} onChange={v => setValues(prev => ({ ...prev, [field.key]: v }))} placeholder={field.placeholder} />
                  ) : (
                    <input type="text" value={values[field.key] ?? ''} onChange={e => setValues(prev => ({ ...prev, [field.key]: e.target.value }))} placeholder={field.placeholder}
                      className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm font-mono text-slate-200 focus:outline-none focus:border-brand-500 transition-colors" />
                  )
                ) : (
                  <div className="flex items-center gap-2 bg-slate-950 rounded-lg px-3 py-2">
                    <code className="text-xs font-mono text-slate-400 flex-1">{storedVal || '(not set)'}</code>
                    <CopyButton value={storedVal} />
                  </div>
                )}
              </div>
            )
          })}
          <div className="flex gap-2 pt-1">
            {editing ? (
              <>
                <button onClick={() => { setEditing(false); setValues({}) }}
                  className="px-3 py-1.5 rounded-lg border border-slate-700 text-xs text-slate-400 hover:text-slate-300 transition-colors">Cancel</button>
                <button onClick={() => mut.mutate()} disabled={mut.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-500 text-white text-xs transition-colors disabled:opacity-60">
                  {mut.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}Save
                </button>
              </>
            ) : (
              <>
                <button onClick={() => { setEditing(true); setValues(Object.fromEntries(platform.fields.map((f: CredentialFieldDef) => [f.key, '']))) }}
                  className="px-3 py-1.5 rounded-lg border border-slate-700 text-xs text-slate-400 hover:text-slate-300 transition-colors">
                  Update Keys
                </button>
                <button onClick={() => deleteMut.mutate()}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-900/50 text-xs text-red-500 hover:text-red-400 hover:border-red-800 transition-colors">
                  <Trash2 className="w-3 h-3" />Remove
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function CredentialsTab() {
  const { data, isLoading } = useQuery({ queryKey: ['integrations'], queryFn: listIntegrations })
  const configured = (data?.platforms ?? []).filter(p => p.has_credentials)
  const unconfigured = (data?.platforms ?? []).filter(p => !p.has_credentials)

  if (isLoading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-400" /></div>

  return (
    <div className="space-y-5">
      <Card title="Configured Credentials" action={<Badge color="green">{configured.length} active</Badge>}>
        {configured.length === 0 ? (
          <p className="text-sm text-slate-500 py-4 text-center">No credentials configured yet. Go to Connections to add a platform.</p>
        ) : (
          <div className="space-y-3">
            {configured.map(p => <CredentialBlock key={p.id} platform={p} />)}
          </div>
        )}
      </Card>

      {unconfigured.length > 0 && (
        <Card title="Available Platforms" action={<Badge color="slate">{unconfigured.length} not configured</Badge>}>
          <div>
            {unconfigured.map(p => (
              <div key={p.id} className="flex items-center gap-3 py-2 border-b border-slate-800/50 last:border-0">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold shrink-0 ${platformBg(p.color)}`}>{p.icon}</div>
                <span className="text-sm text-slate-400 flex-1">{p.label}</span>
                <span className="text-xs text-slate-600">{p.fields.length} field{p.fields.length !== 1 ? 's' : ''}</span>
                <Badge color="slate">Not configured</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="flex items-start gap-3 bg-brand-950/30 border border-brand-900/40 rounded-xl p-4">
        <Lock className="w-4 h-4 text-brand-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-semibold text-brand-300 mb-0.5">Military-grade encryption</p>
          <p className="text-xs text-slate-500">
            All credentials are encrypted with AES-256-GCM before storage. The key is derived from
            your <code className="text-slate-400">SOCIALSCALE_SECRET_KEY</code> env variable and
            never stored in the database.
          </p>
        </div>
      </div>
    </div>
  )
}

// ─── ────────────────────────────────────────────────────────────────────────
//     TAB 3: API Tokens
// ─── ────────────────────────────────────────────────────────────────────────

function ApiTokensTab() {
  const qc = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [newLabel, setNewLabel] = useState('')
  const [newRole, setNewRole] = useState('admin')
  const [createdToken, setCreatedToken] = useState<NewApiToken | null>(null)
  const [revealed, setRevealed] = useState(false)

  const { data, isLoading } = useQuery({ queryKey: ['api-keys'], queryFn: listApiKeys })

  const createMut = useMutation({
    mutationFn: () => createApiKey(newLabel, newRole),
    onSuccess: (t) => {
      setCreatedToken(t)
      setShowCreate(false)
      setNewLabel('')
      setNewRole('admin')
      qc.invalidateQueries({ queryKey: ['api-keys'] })
    },
  })

  const revokeMut = useMutation({
    mutationFn: (id: string) => revokeApiKey(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  })

  return (
    <div className="space-y-5">
      {createdToken && (
        <div className="bg-green-900/20 border border-green-800/50 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <span className="text-sm font-semibold text-green-300">Token created — copy it now!</span>
          </div>
          <p className="text-xs text-slate-400">{createdToken.warning}</p>
          <div className="flex items-center gap-2 bg-slate-950 rounded-lg px-3 py-2 border border-slate-800">
            <code className="text-sm font-mono text-green-300 flex-1 break-all">
              {revealed
                ? createdToken.token
                : `${createdToken.token.substring(0, 10)}${'•'.repeat(40)}${createdToken.token.slice(-4)}`}
            </code>
            <button onClick={() => setRevealed(r => !r)} className="text-slate-500 hover:text-slate-300 p-1">
              {revealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            <CopyButton value={createdToken.token} />
          </div>
          <div className="space-y-1.5">
            <p className="text-xs text-slate-500">Use in CLI or API requests:</p>
            <CliSnippet code={`export SOCIALSCALE_API_KEY=${createdToken.token.substring(0, 14)}...`} />
            <CliSnippet code={`curl -H "Authorization: Bearer ${createdToken.token.substring(0, 14)}..." http://localhost:8080/api/v1/status`} />
          </div>
          <button onClick={() => setCreatedToken(null)} className="text-xs text-slate-500 hover:text-slate-400 transition-colors">Dismiss</button>
        </div>
      )}

      <Card title="API Tokens" action={
        <button onClick={() => setShowCreate(s => !s)}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-brand-700 hover:bg-brand-600 text-white transition-colors">
          <Plus className="w-3.5 h-3.5" />New Token
        </button>
      }>
        {showCreate && (
          <div className="mb-4 p-4 bg-slate-900 border border-slate-800 rounded-xl space-y-3">
            <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wider">Generate New Token</h4>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Label</label>
              <input type="text" value={newLabel} onChange={e => setNewLabel(e.target.value)}
                placeholder="e.g. Dashboard, CI/CD, n8n webhook"
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200
                           placeholder-slate-600 focus:outline-none focus:border-brand-500 transition-colors" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Role</label>
              <select value={newRole} onChange={e => setNewRole(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-brand-500 transition-colors">
                <option value="admin">Admin — full access</option>
                <option value="editor">Editor — read &amp; write, no delete</option>
                <option value="viewer">Viewer — read-only</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowCreate(false)}
                className="px-3 py-1.5 rounded-lg border border-slate-700 text-xs text-slate-400 hover:text-slate-300 transition-colors">Cancel</button>
              <button onClick={() => createMut.mutate()} disabled={!newLabel.trim() || createMut.isPending}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-500 text-white text-xs font-medium transition-colors disabled:opacity-60">
                {createMut.isPending && <Loader2 className="w-3 h-3 animate-spin" />}Generate
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
        ) : (data?.tokens ?? []).length === 0 ? (
          <div className="py-8 text-center">
            <Key className="w-8 h-8 text-slate-700 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No API tokens yet</p>
            <p className="text-xs text-slate-600 mt-1">Create a token to authenticate CLI and API requests</p>
          </div>
        ) : (
          <div>
            {(data?.tokens ?? []).map((t: ApiToken) => (
              <div key={t.id} className="flex items-center gap-3 py-2.5 border-b border-slate-800/50 last:border-0">
                <Key className="w-4 h-4 text-slate-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-200">{t.label}</span>
                    <Badge color={t.role === 'admin' ? 'purple' : t.role === 'editor' ? 'yellow' : 'slate'}>{t.role}</Badge>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <code className="text-xs font-mono text-slate-600">{t.token_preview}</code>
                    <span className="text-xs text-slate-700">·</span>
                    <span className="text-xs text-slate-600">Created {new Date(t.created_at).toLocaleDateString()}</span>
                    {t.expires_at && <span className="text-xs text-yellow-600">· Expires {new Date(t.expires_at).toLocaleDateString()}</span>}
                  </div>
                </div>
                <button onClick={() => { if (confirm('Revoke this token? This cannot be undone.')) revokeMut.mutate(t.id) }}
                  className="p-1.5 rounded-lg hover:bg-red-900/20 text-slate-600 hover:text-red-400 transition-colors" title="Revoke token">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Usage Examples">
        <div className="space-y-3">
          <div>
            <p className="text-xs text-slate-500 mb-1.5">REST API</p>
            <CliSnippet code='curl -H "Authorization: Bearer YOUR_TOKEN" http://localhost:8080/api/v1/status' />
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-1.5">CLI Authentication</p>
            <CliSnippet code="socialscale auth login" />
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-1.5">Environment Variable</p>
            <CliSnippet code="export SOCIALSCALE_API_KEY=ss_your_token_here" />
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-1.5">n8n / Zapier webhook header</p>
            <CliSnippet code="Authorization: Bearer YOUR_TOKEN" />
          </div>
        </div>
      </Card>
    </div>
  )
}

// ─── ────────────────────────────────────────────────────────────────────────
//     TAB 4: Setup Guide
// ─── ────────────────────────────────────────────────────────────────────────

const GUIDES = [
  {
    id: 'x', label: 'X (Twitter)', color: '#000000', icon: '𝕏',
    cli: 'socialscale config set x.api_key YOUR_API_KEY',
    steps: [
      { text: 'Go to the X Developer Portal', link: 'https://developer.twitter.com/en/portal/dashboard' },
      { text: 'Create a new Project and App (or select an existing one)' },
      { text: 'Under Keys and Tokens, generate an API Key & Secret' },
      { text: 'Enable OAuth 2.0 with PKCE in App Settings → User authentication' },
      { text: 'Set Callback URI: your-domain.com/oauth/callback/x' },
      { text: 'Paste the API Key and Secret into SocialScale Credentials above' },
    ],
  },
  {
    id: 'linkedin', label: 'LinkedIn', color: '#0A66C2', icon: 'in',
    cli: 'socialscale config set linkedin.client_id YOUR_CLIENT_ID',
    steps: [
      { text: 'Visit the LinkedIn Developer Portal', link: 'https://www.linkedin.com/developers/apps' },
      { text: 'Create an app with your company LinkedIn page' },
      { text: 'Under Auth tab, add your redirect URL' },
      { text: 'Request products: "Sign In with LinkedIn" and "Share on LinkedIn"' },
      { text: 'Copy the Client ID and Client Secret from the Auth tab' },
    ],
  },
  {
    id: 'facebook', label: 'Facebook / Instagram', color: '#1877F2', icon: 'f',
    cli: 'socialscale config set facebook.app_id YOUR_APP_ID',
    steps: [
      { text: 'Open Meta for Developers', link: 'https://developers.facebook.com/apps' },
      { text: 'Create a new app, select "Business" type' },
      { text: 'Add "Facebook Login" and "Instagram Basic Display" products' },
      { text: 'In Settings → Basic: copy App ID and App Secret' },
      { text: 'Under Facebook Login → Settings: add your Redirect URI' },
      { text: 'Set App Mode to Live for real account access' },
    ],
  },
  {
    id: 'tiktok', label: 'TikTok', color: '#010101', icon: 'TT',
    cli: 'socialscale config set tiktok.client_id YOUR_CLIENT_KEY',
    steps: [
      { text: 'Go to TikTok for Developers', link: 'https://developers.tiktok.com/apps' },
      { text: 'Create a new app and fill in app details' },
      { text: 'Add the "Login Kit" product' },
      { text: 'Under Redirect Domain, add your deployment domain' },
      { text: 'Copy Client Key and Client Secret from the app page' },
    ],
  },
  {
    id: 'youtube', label: 'YouTube', color: '#FF0000', icon: 'YT',
    cli: 'socialscale config set youtube.client_id YOUR_CLIENT_ID',
    steps: [
      { text: 'Open Google Cloud Console', link: 'https://console.cloud.google.com/apis/credentials' },
      { text: 'Create a project and enable YouTube Data API v3' },
      { text: 'Create OAuth 2.0 credentials (Web Application type)' },
      { text: 'Add authorized redirect URIs for your domain' },
      { text: 'Copy Client ID and Client Secret' },
    ],
  },
  {
    id: 'openai', label: 'OpenAI', color: '#10A37F', icon: 'AI',
    cli: 'socialscale config set openai.api_key sk-proj-...',
    steps: [
      { text: 'Log in to OpenAI Platform', link: 'https://platform.openai.com/api-keys' },
      { text: 'Click "+ Create new secret key"' },
      { text: 'Name it "SocialScale" and set permissions' },
      { text: 'Copy the key immediately — shown only once' },
      { text: 'Paste it into SocialScale OpenAI Credentials' },
    ],
  },
  {
    id: 'anthropic', label: 'Anthropic (Claude)', color: '#D97757', icon: 'CL',
    cli: 'export ANTHROPIC_API_KEY=sk-ant-api03-...',
    steps: [
      { text: 'Open Anthropic Console', link: 'https://console.anthropic.com/keys' },
      { text: 'Click "Create Key" in the API Keys section' },
      { text: 'Name the key and select workspace permissions' },
      { text: 'Copy the key — shown only once' },
      { text: 'Set it in SocialScale Credentials or via env var' },
    ],
  },
]

function SetupGuideTab() {
  const [active, setActive] = useState('x')
  const guide = GUIDES.find(g => g.id === active) ?? GUIDES[0]

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        {GUIDES.map(g => (
          <button key={g.id} onClick={() => setActive(g.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors
                        ${active === g.id
                          ? 'bg-brand-700/50 text-brand-200 border border-brand-700'
                          : 'bg-slate-800 text-slate-400 hover:text-slate-300 border border-transparent'}`}>
            <span className={`w-5 h-5 rounded flex items-center justify-center text-white text-[10px] font-bold ${platformBg(g.color)}`}>
              {g.icon}
            </span>
            {g.label}
          </button>
        ))}
      </div>

      <Card title={`${guide.label} Setup Guide`}>
        <ol className="space-y-3">
          {guide.steps.map((step, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-slate-800 text-slate-400 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                {i + 1}
              </span>
              <div className="flex-1">
                <p className="text-sm text-slate-300">{step.text}</p>
                {step.link && (
                  <a href={step.link} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-brand-400 hover:text-brand-300 mt-1 transition-colors">
                    <ExternalLink className="w-3 h-3" />Open {guide.label} Console
                  </a>
                )}
              </div>
            </li>
          ))}
        </ol>
      </Card>

      <Card title="Terminal Commands">
        <div className="space-y-2">
          <p className="text-xs text-slate-500 mb-2">After completing the steps above:</p>
          <CliSnippet code={guide.cli} />
          <CliSnippet code={`socialscale integrations test ${guide.id}`} />
          <CliSnippet code={`socialscale accounts connect ${guide.id}`} />
        </div>
      </Card>

      <Card title="Environment Variable Alternative">
        <div className="space-y-2">
          <p className="text-xs text-slate-500 mb-2">Set credentials via <code className="text-slate-400">.env</code> file:</p>
          <div className="bg-slate-950 border border-slate-800 rounded-lg p-3 space-y-0.5">
            <code className="block text-xs font-mono text-slate-400">
              # .env — required on server startup<br />
              SOCIALSCALE_SECRET_KEY=your-strong-32-char-secret<br />
              SOCIALSCALE_API_KEY=ss_your_workspace_token<br />
              <br />
              # Social platforms<br />
              X_API_KEY=LQTo7jQ9IO1eIjwcOtIwEvllM<br />
              X_API_SECRET=7jaWEAiQgvKZ...<br />
              LINKEDIN_CLIENT_ID=86rf19nwodzga4<br />
              LINKEDIN_CLIENT_SECRET=WPL_AP1.hLdS0z...<br />
              FACEBOOK_APP_ID=1403947518181602<br />
              FACEBOOK_APP_SECRET=f69ef199e0ed...<br />
              YOUTUBE_CLIENT_ID=385325625223-...<br />
              TIKTOK_CLIENT_ID=awgy3kg2arekpf9b<br />
              <br />
              # AI providers<br />
              OPENAI_API_KEY=sk-proj-...<br />
              ANTHROPIC_API_KEY=sk-ant-api03-...
            </code>
          </div>
        </div>
      </Card>
    </div>
  )
}

// ─── ────────────────────────────────────────────────────────────────────────
//     System Settings (always shown below tabs)
// ─── ────────────────────────────────────────────────────────────────────────

function SystemSettings() {
  const [autonomyMode, setAutonomyMode] = useState<'copilot' | 'autopilot'>('copilot')
  const [notifications, setNotifications] = useState({
    anomaly_alerts: true,
    weekly_digest: true,
    experiment_complete: true,
    inbox_urgent: true,
  })

  return (
    <div className="space-y-5">
      <Card title="Autonomy Mode">
        <div className="flex gap-3">
          {[
            { id: 'copilot' as const, icon: <Shield className="w-4 h-4 text-yellow-400" />, color: 'yellow' as const, desc: 'Agents suggest. You approve before anything posts.' },
            { id: 'autopilot' as const, icon: <Zap className="w-4 h-4 text-green-400" />, color: 'green' as const, desc: 'Agents act autonomously within your policies.' },
          ].map(m => (
            <button key={m.id} onClick={() => setAutonomyMode(m.id)}
              className={`flex-1 p-3 rounded-xl border text-left transition-colors ${
                autonomyMode === m.id ? 'border-brand-600 bg-brand-900/20' : 'border-slate-700 hover:border-slate-600'}`}>
              <div className="flex items-center gap-2 mb-1">
                {m.icon}
                <span className="text-sm font-semibold text-slate-200 capitalize">{m.id}</span>
                {autonomyMode === m.id && <Badge color={m.color}>Active</Badge>}
              </div>
              <p className="text-xs text-slate-500">{m.desc}</p>
            </button>
          ))}
        </div>
      </Card>

      <Card title="Notifications">
        <div className="space-y-3">
          {(Object.entries(notifications) as [keyof typeof notifications, boolean][]).map(([key, val]) => (
            <div key={key} className="flex items-center justify-between py-1">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-slate-500" />
                <span className="text-sm text-slate-300 capitalize">{key.replace(/_/g, ' ')}</span>
              </div>
              <button onClick={() => setNotifications(n => ({ ...n, [key]: !val }))}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${val ? 'bg-brand-600' : 'bg-slate-700'}`}>
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${val ? 'translate-x-4' : 'translate-x-1'}`} />
              </button>
            </div>
          ))}
        </div>
      </Card>

      <Card title="About">
        <div className="space-y-1 text-xs text-slate-500">
          <div className="flex justify-between"><span>Version</span><span className="text-slate-400">0.2.0</span></div>
          <div className="flex justify-between"><span>API</span><span className="text-slate-400">localhost:8080</span></div>
          <div className="flex justify-between"><span>MCP Protocol</span><span className="text-slate-400">2025-11-25</span></div>
          <div className="flex justify-between"><span>Encryption</span><span className="text-green-400">AES-256-GCM ✓</span></div>
          <div className="flex justify-between"><span>Meta API</span><span className="text-slate-400">v23.0</span></div>
        </div>
      </Card>
    </div>
  )
}

// ─── Workspace Tab ───────────────────────────────────────────────────────────

function WorkspaceTab() {
  const [name, setName] = useState('My Workspace')
  const [timezone, setTimezone] = useState('America/New_York')
  const [saved, setSaved] = useState(false)

  function handleSave() {
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  return (
    <div className="space-y-5">
      <Card title="Workspace Settings">
        <div className="space-y-4">
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Workspace Name</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full bg-slate-800 border border-surface-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-500"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Timezone</label>
            <select
              value={timezone}
              onChange={e => setTimezone(e.target.value)}
              className="w-full bg-slate-800 border border-surface-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-500"
            >
              {[
                'America/New_York', 'America/Chicago', 'America/Denver',
                'America/Los_Angeles', 'Europe/London', 'Europe/Paris',
                'Asia/Tokyo', 'Asia/Singapore', 'Australia/Sydney',
              ].map(tz => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Default Publish Platforms</label>
            <div className="flex flex-wrap gap-2">
              {['instagram', 'twitter', 'linkedin', 'tiktok'].map(p => (
                <span key={p} className="text-xs bg-slate-800 border border-surface-border px-3 py-1 rounded-full capitalize text-slate-400">{p}</span>
              ))}
            </div>
            <p className="text-[11px] text-slate-600 mt-1.5">Configured via connected accounts in Connections tab</p>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Autonomy Mode</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {[
                { id: 'manual', label: 'Manual', desc: 'All actions require confirmation' },
                { id: 'copilot', label: 'Copilot', desc: 'Agents suggest, human approves' },
                { id: 'autopilot', label: 'Autopilot', desc: 'Agents act autonomously' },
              ].map(m => (
                <div key={m.id} className={`p-3 rounded-xl border text-left cursor-pointer transition-colors ${
                  m.id === 'copilot'
                    ? 'bg-brand-700/20 border-brand-700/50'
                    : 'bg-slate-800/30 border-surface-border hover:border-slate-600'
                }`}>
                  <div className={`text-xs font-semibold mb-0.5 ${m.id === 'copilot' ? 'text-brand-200' : 'text-slate-300'}`}>{m.label}</div>
                  <div className="text-[10px] text-slate-600 leading-tight">{m.desc}</div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-600 mt-1.5">Change via CLI: <code className="bg-slate-800 px-1 rounded">socialscale config set autonomy_mode autopilot</code></p>
          </div>
          <button
            onClick={handleSave}
            className={`flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg transition-colors ${
              saved ? 'bg-green-700/40 text-green-300' : 'bg-brand-600 hover:bg-brand-700 text-white'
            }`}
          >
            {saved ? <><Check className="w-4 h-4" /> Saved</> : 'Save Changes'}
          </button>
        </div>
      </Card>

      <Card title="Team Members">
        <div className="space-y-3">
          {[
            { name: 'Admin User', email: 'admin@example.com', role: 'admin' },
            { name: 'Content Manager', email: 'content@example.com', role: 'manager' },
          ].map(m => (
            <div key={m.email} className="flex items-center gap-3 p-3 bg-slate-800/30 rounded-xl border border-surface-border">
              <div className="w-8 h-8 rounded-full bg-brand-700/50 flex items-center justify-center text-xs font-bold text-brand-200">
                {m.name.slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-200">{m.name}</div>
                <div className="text-xs text-slate-500">{m.email}</div>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${m.role === 'admin' ? 'bg-brand-700/30 text-brand-300' : 'bg-slate-700 text-slate-400'}`}>
                {m.role}
              </span>
            </div>
          ))}
          <button className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors mt-1">
            <Plus className="w-3.5 h-3.5" /> Invite team member
          </button>
        </div>
      </Card>
    </div>
  )
}

// ─── LLM Config Tab ───────────────────────────────────────────────────────────

function LlmConfigTab() {
  const [provider, setProvider] = useState('anthropic')
  const [model, setModel] = useState('claude-sonnet-4-6')
  const [budget, setBudget] = useState('10.00')
  const [showKey, setShowKey] = useState(false)

  const PROVIDERS: Record<string, { label: string; models: string[] }> = {
    anthropic: {
      label: 'Anthropic (Claude)',
      models: ['claude-opus-4-6', 'claude-sonnet-4-6', 'claude-haiku-4-5-20251001'],
    },
    openai: {
      label: 'OpenAI',
      models: ['gpt-4o', 'gpt-4o-mini', 'o1', 'o1-mini'],
    },
    google: {
      label: 'Google (Gemini)',
      models: ['gemini-2.0-flash', 'gemini-1.5-pro'],
    },
  }

  const TASK_MODELS: { task: string; desc: string; default: string }[] = [
    { task: 'Content generation', desc: 'Drafting posts and captions', default: 'claude-sonnet-4-6' },
    { task: 'Trend analysis', desc: 'Analyzing trends and signals', default: 'claude-haiku-4-5-20251001' },
    { task: 'Compliance review', desc: 'Brand and policy checking', default: 'claude-sonnet-4-6' },
    { task: 'Ad optimization', desc: 'Budget and creative decisions', default: 'claude-opus-4-6' },
  ]

  return (
    <div className="space-y-5">
      <Card title="LLM Provider">
        <div className="space-y-4">
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Provider</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {Object.entries(PROVIDERS).map(([id, p]) => (
                <button
                  key={id}
                  onClick={() => { setProvider(id); setModel(p.models[0]) }}
                  className={`p-3 rounded-xl border text-left text-xs transition-colors ${
                    provider === id
                      ? 'bg-brand-700/20 border-brand-700/50 text-brand-200'
                      : 'bg-slate-800/30 border-surface-border text-slate-400 hover:text-slate-300'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Default Model</label>
            <select
              value={model}
              onChange={e => setModel(e.target.value)}
              className="w-full bg-slate-800 border border-surface-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-500"
            >
              {PROVIDERS[provider]?.models.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">API Key</label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <input
                  type={showKey ? 'text' : 'password'}
                  defaultValue="sk-ant-api03-••••••••••••••••••••"
                  className="w-full bg-slate-800 border border-surface-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-500 pr-10"
                />
                <button
                  onClick={() => setShowKey(v => !v)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <button className="bg-slate-700 hover:bg-slate-600 text-xs px-3 rounded-lg transition-colors">Save</button>
            </div>
            <p className="text-[11px] text-slate-600 mt-1">Or set via env: <code className="bg-slate-800 px-1 rounded">ANTHROPIC_API_KEY</code></p>
          </div>
        </div>
      </Card>

      <Card title="Model Preferences per Task">
        <div className="space-y-2">
          {TASK_MODELS.map(t => (
            <div key={t.task} className="flex flex-wrap sm:flex-nowrap items-center gap-3 p-3 bg-slate-800/30 rounded-xl border border-surface-border">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-slate-300">{t.task}</div>
                <div className="text-[11px] text-slate-600">{t.desc}</div>
              </div>
              <select className="w-full sm:w-auto bg-slate-800 border border-surface-border rounded-lg px-2 py-1 text-xs focus:outline-none">
                <option>{t.default}</option>
              </select>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Token Budget">
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Monthly budget cap (USD)</label>
            <div className="flex gap-2 items-center">
              <span className="text-slate-500 text-sm">$</span>
              <input
                type="number"
                value={budget}
                onChange={e => setBudget(e.target.value)}
                min="0"
                step="1"
                className="w-32 bg-slate-800 border border-surface-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-500"
              />
              <span className="text-xs text-slate-500">/ month</span>
            </div>
          </div>
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>Current month spend</span>
            <span className="text-slate-300 font-mono">$2.47</span>
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div className="h-full bg-green-500 rounded-full" style={{ width: `${(2.47 / parseFloat(budget || '10')) * 100}%` }} />
          </div>
          <p className="text-[11px] text-slate-600">Budget alerts fire at 80% and 100% of cap.</p>
        </div>
      </Card>
    </div>
  )
}

// ─── Alerts Tab ───────────────────────────────────────────────────────────────

function AlertsTab() {
  type AlertChannel = 'email' | 'webhook' | 'slack'
  type AlertItem = { id: string; label: string; desc: string; enabled: boolean }

  const [channels, setChannels] = useState<Set<AlertChannel>>(new Set(['email']))
  const [alerts, setAlerts] = useState<AlertItem[]>([
    { id: 'spend_80', label: 'Spend at 80%', desc: 'When monthly LLM spend reaches 80% of budget', enabled: true },
    { id: 'spend_100', label: 'Spend at 100%', desc: 'When monthly LLM spend hits the cap', enabled: true },
    { id: 'agent_error', label: 'Agent Error', desc: 'When any agent encounters a critical error', enabled: true },
    { id: 'pending_approval', label: 'Pending Approval', desc: 'When a new approval request needs review', enabled: true },
    { id: 'policy_violation', label: 'Policy Violation', desc: 'When content fails compliance check', enabled: false },
    { id: 'token_expiry', label: 'Token Expiry', desc: 'When a credential expires within 7 days', enabled: true },
    { id: 'campaign_pause', label: 'Campaign Paused', desc: 'When an ad campaign is auto-paused', enabled: false },
    { id: 'weekly_digest', label: 'Weekly Digest', desc: 'Weekly performance summary email', enabled: true },
  ])

  const toggleChannel = (c: AlertChannel) => {
    setChannels(prev => {
      const next = new Set(prev)
      if (next.has(c)) { next.delete(c) } else { next.add(c) }
      return next
    })
  }

  const toggleAlert = (id: string) => {
    setAlerts(prev => prev.map(a => a.id === id ? { ...a, enabled: !a.enabled } : a))
  }

  return (
    <div className="space-y-5">
      <Card title="Notification Channels">
        <div className="space-y-3">
          {[
            { id: 'email' as AlertChannel, label: 'Email', placeholder: 'alerts@yourcompany.com' },
            { id: 'webhook' as AlertChannel, label: 'Webhook', placeholder: 'https://hooks.yourapp.com/...' },
            { id: 'slack' as AlertChannel, label: 'Slack Webhook', placeholder: 'https://hooks.slack.com/services/...' },
          ].map(ch => (
            <div key={ch.id} className={`p-3 rounded-xl border transition-colors ${
              channels.has(ch.id) ? 'border-brand-700/50 bg-brand-900/10' : 'border-surface-border bg-slate-800/20'
            }`}>
              <div className="flex items-center gap-3 mb-2">
                <button
                  onClick={() => toggleChannel(ch.id)}
                  className={`w-8 h-4.5 rounded-full relative transition-colors ${channels.has(ch.id) ? 'bg-brand-500' : 'bg-slate-700'}`}
                  style={{ minWidth: 32, height: 18 }}
                >
                  <span className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white transition-all ${channels.has(ch.id) ? 'left-4' : 'left-0.5'}`} />
                </button>
                <span className={`text-sm font-medium ${channels.has(ch.id) ? 'text-slate-200' : 'text-slate-500'}`}>{ch.label}</span>
              </div>
              {channels.has(ch.id) && (
                <input
                  placeholder={ch.placeholder}
                  className="w-full bg-slate-800 border border-surface-border rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-brand-500"
                />
              )}
            </div>
          ))}
        </div>
      </Card>

      <Card title="Alert Rules">
        <div className="space-y-2">
          {alerts.map(a => (
            <div key={a.id} className="flex items-start gap-3 p-3 bg-slate-800/30 rounded-xl border border-surface-border">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-slate-300">{a.label}</div>
                <div className="text-[11px] text-slate-600 mt-0.5">{a.desc}</div>
              </div>
              <button
                onClick={() => toggleAlert(a.id)}
                className={`shrink-0 w-8 rounded-full relative transition-colors mt-0.5 ${a.enabled ? 'bg-brand-500' : 'bg-slate-700'}`}
                style={{ minWidth: 32, height: 18 }}
              >
                <span className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white transition-all ${a.enabled ? 'left-4' : 'left-0.5'}`} />
              </button>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-600 mt-3">
          {alerts.filter(a => a.enabled).length} of {alerts.length} alerts enabled
        </p>
      </Card>
    </div>
  )
}

// ─── ────────────────────────────────────────────────────────────────────────
//     Main SettingsView
// ─── ────────────────────────────────────────────────────────────────────────

const TABS: { id: Tab; label: string; icon: React.ReactNode; description: string }[] = [
  { id: 'platforms', label: 'Connections', icon: <Globe className="w-4 h-4" />, description: 'Connect social accounts via OAuth' },
  { id: 'credentials', label: 'Credentials', icon: <Lock className="w-4 h-4" />, description: 'Manage stored API keys & secrets' },
  { id: 'tokens', label: 'API Tokens', icon: <Key className="w-4 h-4" />, description: 'SocialScale access tokens' },
  { id: 'workspace', label: 'Workspace', icon: <Building2 className="w-4 h-4" />, description: 'Name, timezone, team members' },
  { id: 'llm', label: 'AI / LLM', icon: <Cpu className="w-4 h-4" />, description: 'Provider, model, token budget' },
  { id: 'alerts', label: 'Alerts', icon: <Bell className="w-4 h-4" />, description: 'Notification rules & channels' },
  { id: 'guide', label: 'Setup Guide', icon: <Zap className="w-4 h-4" />, description: 'Step-by-step platform guides' },
]

export default function SettingsView() {
  const [tab, setTab] = useState<Tab>('platforms')

  const tabContent = (
    <>
      {tab === 'platforms' && <PlatformsTab />}
      {tab === 'credentials' && <CredentialsTab />}
      {tab === 'tokens' && <ApiTokensTab />}
      {tab === 'workspace' && <WorkspaceTab />}
      {tab === 'llm' && <LlmConfigTab />}
      {tab === 'alerts' && <AlertsTab />}
      {tab === 'guide' && <SetupGuideTab />}
    </>
  )

  return (
    <div>
      <PageHeader
        title="Settings & Integrations"
        subtitle="Credential vault · OAuth connections · API keys · Setup guides"
      />

      {/* ── Mobile: horizontal scrollable tab bar ─────────────────────── */}
      <div className="sm:hidden border-b border-surface-border px-3 py-2">
        <div
          className="flex gap-1 overflow-x-auto"
          style={{ scrollbarWidth: 'none' }}
        >
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium whitespace-nowrap shrink-0 transition-colors border ${
                tab === t.id
                  ? 'bg-brand-700/30 border-brand-700/50 text-brand-200'
                  : 'border-transparent text-slate-500 hover:text-slate-300'
              }`}
            >
              <span className="shrink-0">{t.icon}</span>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Mobile: full-width content ────────────────────────────────── */}
      <div className="sm:hidden p-3 space-y-4">
        {tabContent}
        <SystemSettings />
      </div>

      {/* ── Tablet+: sidebar + content layout ────────────────────────── */}
      <div className="hidden sm:flex gap-4 lg:gap-6 p-4 lg:p-6">
        {/* Sidebar */}
        <div className="w-44 lg:w-52 shrink-0 space-y-1">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`w-full flex items-start gap-3 px-3 py-3 rounded-xl text-left transition-colors ${
                tab === t.id
                  ? 'bg-brand-700/30 border border-brand-700/50 text-brand-200'
                  : 'hover:bg-slate-800/60 text-slate-400 hover:text-slate-300 border border-transparent'}`}>
              <span className={`mt-0.5 shrink-0 ${tab === t.id ? 'text-brand-300' : 'text-slate-500'}`}>{t.icon}</span>
              <div>
                <div className="text-sm font-medium">{t.label}</div>
                <div className="text-xs text-slate-600 mt-0.5 leading-tight">{t.description}</div>
              </div>
            </button>
          ))}
          <div className="mt-4 p-3 bg-brand-950/40 border border-brand-900/30 rounded-xl">
            <div className="flex items-center gap-1.5 mb-1">
              <Lock className="w-3.5 h-3.5 text-brand-400" />
              <span className="text-xs font-semibold text-brand-300">Vault Secured</span>
            </div>
            <p className="text-[11px] text-slate-600 leading-tight">AES-256-GCM · Zero plaintext storage · Key-derived from env</p>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {tabContent}
          <div className="mt-6">
            <SystemSettings />
          </div>
        </div>
      </div>
    </div>
  )
}
